import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Check, LoaderCircle, Mail } from 'lucide-react';
import { Button } from '../ds';
import { cx, disc } from '../ds/classes';
import { ErrorNote } from '../workspace/ErrorNote';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import { track } from '../../utils/analytics';
import { AuthCard } from './AuthPages';

const StatusDisc = ({ icon: Icon }) => (
  <span className={cx(disc, 'w-10 h-10 bg-raised text-ink-soft')}>
    <Icon size={20} strokeWidth={2.5} aria-hidden="true" />
  </span>
);

/** After signing up: check your inbox, or get a new link */
export const VerificationPending = () => {
  const { user, authFetch } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // 'success' | 'error' | null

  const handleResendEmail = async () => {
    if (resending) return;
    setResending(true);
    setResendStatus(null);
    try {
      await authFetch('/auth/resend-verification', { method: 'POST' });
      setResendStatus('success');
    } catch (err) {
      setResendStatus('error');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthCard
      title="Check your email"
      subtitle={<>We sent a link to <span className="font-bold text-ink">{user?.email || 'your email'}</span>. Open it to verify your account and start analyzing.</>}
    >
      <div className="flex flex-col gap-space-3">
        <Button as={Link} to="/projects" className="w-full">Go to your projects</Button>
        <Button variant="secondary" icon={Mail} onClick={handleResendEmail} disabled={resending} className="w-full">
          {resending ? 'Sending…' : 'Send a new link'}
        </Button>
        {resendStatus === 'success' && (
          <p role="status" className="m-0 flex items-center gap-space-2 text-small text-ink-soft">
            <Check size={16} strokeWidth={2.5} aria-hidden="true" />
            A new link is on its way. The old one no longer works.
          </p>
        )}
        {resendStatus === 'error' && <ErrorNote>We couldn't send the email. Please try again in a few minutes.</ErrorNote>}
      </div>
    </AuthCard>
  );
};

/** /verify?token=…: checks the emailed link, then heads to the projects page */
export const VerifyPage = () => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = new URLSearchParams(location.search).get('token');
    if (!token) {
      setStatus('error');
      return undefined;
    }
    let redirect;
    const verifyAccount = async () => {
      try {
        const data = await apiFetch(`/auth/verify?token=${encodeURIComponent(token)}`, { method: 'POST' });
        if (data?.access_token && data?.user) login(data.access_token, data.user);
        track('email_verified');
        setStatus('success');
        redirect = setTimeout(() => navigate('/projects'), 3000);
      } catch (err) {
        setStatus('error');
      }
    };
    verifyAccount();
    return () => clearTimeout(redirect);
  }, [location.search, navigate, login]);

  if (status === 'loading') {
    return (
      <AuthCard title="Verifying your account" subtitle="This only takes a moment.">
        <div role="status" className="flex items-center gap-space-3 text-small text-muted">
          <LoaderCircle size={20} strokeWidth={2.5} aria-hidden="true" className="animate-spin motion-reduce:animate-none text-amber" />
          Checking your link…
        </div>
      </AuthCard>
    );
  }
  if (status === 'success') {
    return (
      <AuthCard title="You're verified" subtitle="Your account is ready. Taking you to your projects…">
        <div className="flex items-center gap-space-3">
          <StatusDisc icon={Check} />
          <Button as={Link} to="/projects" variant="text">Go now</Button>
        </div>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="That link didn't work" subtitle="It may have expired, or a newer link was sent after it.">
      <div className="flex flex-col gap-space-4">
        <ErrorNote>This verification link is invalid or expired.</ErrorNote>
        <Button as={Link} to="/verify-pending" className="w-full">Get a new link</Button>
      </div>
    </AuthCard>
  );
};
